import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, ArrowUpRight, PlayCircle, Receipt, Heart, Download, ShoppingBag, Shield } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { API, useAuth } from "@/lib/auth";

export default function Account() {
  const { user, logout, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [wishlistProducts, setWishlistProducts] = useState([]);
  const [tab, setTab] = useState("library");
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/", { replace: true }); return; }
    axios.get(`${API}/me/entitlements`).then((r) => setEntitlements(r.data)).catch(() => {});
    axios.get(`${API}/me/orders`).then((r) => setOrders(r.data)).catch(() => {});
    axios.get(`${API}/me/wishlist`).then((r) => setWishlist(r.data)).catch(() => {});
    axios.get(`${API}/me/is_admin`).then((r) => setIsAdmin(r.data.is_admin)).catch(() => {});
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!wishlist.length) { setWishlistProducts([]); return; }
    Promise.all(
      wishlist.map((slug) => axios.get(`${API}/shop/products/${slug}`).then((r) => r.data).catch(() => null))
    ).then((arr) => setWishlistProducts(arr.filter(Boolean)));
  }, [wishlist]);

  const removeFromWishlist = async (slug) => {
    setWishlist((w) => w.filter((s) => s !== slug));
    await axios.delete(`${API}/me/wishlist/${slug}`);
  };

  if (loading || !user) {
    return <div className="pt-40 pb-24 text-center text-[hsl(var(--onyx)/0.5)]">Loading…</div>;
  }

  const courseEnts = entitlements.filter((e) => e.kind === "course" || !e.kind);
  const downloadEnts = entitlements.filter((e) => e.kind === "download");

  return (
    <div data-testid="page-account" className="pt-32 pb-24">
      <div className="mx-auto max-w-[1200px] px-6 sm:px-10">
        <div className="grid gap-8 lg:grid-cols-12 items-center mb-12">
          <div className="lg:col-span-8 flex items-center gap-5">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-white shadow" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-blush flex items-center justify-center font-display text-2xl">
                {user.name?.[0]?.toUpperCase()}
              </div>
            )}
            <div>
              <div className="overline">Welcome back</div>
              <h1 className="font-display text-4xl sm:text-5xl mt-1 tracking-tight">{user.name}</h1>
              <div className="text-sm text-[hsl(var(--onyx)/0.55)] mt-1">{user.email}</div>
            </div>
          </div>
          <div className="lg:col-span-4 lg:text-right flex flex-wrap justify-end gap-2">
            {isAdmin && (
              <Link
                to="/admin"
                data-testid="account-admin-link"
                className="btn-pill btn-blush"
              >
                <Shield size={16} /> Admin
              </Link>
            )}
            <button onClick={logout} data-testid="account-logout" className="btn-pill btn-outline">
              Sign out <LogOut size={16} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { k: "library", l: "My library" },
            { k: "orders", l: "Orders" },
            { k: "wishlist", l: "Wishlist" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`account-tab-${t.k}`}
              className={`px-5 py-2 rounded-full text-sm transition-colors duration-300 ${
                tab === t.k
                  ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                  : "bg-white border border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.65)] hover:text-[hsl(var(--onyx))]"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "library" && (
          entitlements.length === 0 ? (
            <Reveal className="rounded-[2rem] border border-dashed border-[hsl(var(--onyx)/0.15)] p-12 text-center bg-white">
              <div className="overline">Your personal library</div>
              <h2 className="font-display text-3xl mt-3">You haven't purchased anything yet.</h2>
              <p className="mt-3 text-sm text-[hsl(var(--onyx)/0.6)] max-w-md mx-auto">
                Courses you enrol in and digital downloads you buy will appear here to access anytime.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link to="/academy" data-testid="library-academy" className="btn-pill btn-primary">
                  Browse Academy <ArrowUpRight size={16} />
                </Link>
                <Link to="/shop" data-testid="library-shop" className="btn-pill btn-outline">
                  Explore Shop
                </Link>
              </div>
            </Reveal>
          ) : (
            <div className="space-y-10">
              {courseEnts.length > 0 && (
                <div>
                  <div className="overline mb-4">Courses</div>
                  <Stagger className="grid gap-6 sm:grid-cols-2">
                    {courseEnts.map((e) => (
                      <Item key={e.slug || e.course_slug}>
                        <div
                          data-testid={`entitlement-${e.slug || e.course_slug}`}
                          className="rounded-3xl p-7 bg-white border border-[hsl(var(--onyx)/0.06)] flex items-center gap-5"
                        >
                          <div className="w-14 h-14 rounded-2xl bg-blush flex items-center justify-center">
                            <PlayCircle size={26} />
                          </div>
                          <div className="flex-1">
                            <div className="overline">Course access</div>
                            <div className="font-display text-2xl mt-1">
                              {e.name || e.course_name}
                            </div>
                            <div className="text-xs text-[hsl(var(--onyx)/0.5)] mt-1">
                              Unlocked {new Date(e.granted_at).toLocaleDateString()}
                            </div>
                          </div>
                          <Link
                            to={`/academy/${e.slug || e.course_slug}`}
                            className="btn-pill btn-primary !py-2 !px-4 !text-xs"
                          >
                            Open
                          </Link>
                        </div>
                      </Item>
                    ))}
                  </Stagger>
                </div>
              )}
              {downloadEnts.length > 0 && (
                <div>
                  <div className="overline mb-4">Digital downloads</div>
                  <Stagger className="grid gap-6 sm:grid-cols-2">
                    {downloadEnts.map((e) => (
                      <Item key={e.slug}>
                        <div
                          data-testid={`download-${e.slug}`}
                          className="rounded-3xl p-7 bg-white border border-[hsl(var(--onyx)/0.06)] flex items-center gap-5"
                        >
                          <div className="w-14 h-14 rounded-2xl bg-blush flex items-center justify-center">
                            <Download size={22} />
                          </div>
                          <div className="flex-1">
                            <div className="overline">Digital</div>
                            <div className="font-display text-2xl mt-1">{e.name}</div>
                            <div className="text-xs text-[hsl(var(--onyx)/0.5)] mt-1">
                              Available since {new Date(e.granted_at).toLocaleDateString()}
                            </div>
                          </div>
                          <button
                            className="btn-pill btn-outline !py-2 !px-4 !text-xs"
                            title="Download link will be enabled shortly"
                          >
                            Download
                          </button>
                        </div>
                      </Item>
                    ))}
                  </Stagger>
                </div>
              )}
            </div>
          )
        )}

        {tab === "orders" && (
          orders.length === 0 ? (
            <div className="rounded-[2rem] border border-dashed border-[hsl(var(--onyx)/0.15)] p-12 text-center bg-white">
              <div className="overline">No orders yet</div>
              <h2 className="font-display text-3xl mt-3">Your paid orders will appear here.</h2>
            </div>
          ) : (
            <div className="rounded-[2rem] overflow-hidden border border-[hsl(var(--onyx)/0.08)] bg-white">
              {orders.map((o) => (
                <div
                  key={o.session_id}
                  data-testid={`order-${o.session_id}`}
                  className="flex flex-wrap items-center gap-4 p-6 border-b last:border-b-0 border-[hsl(var(--onyx)/0.06)]"
                >
                  <div className="w-10 h-10 rounded-full bg-blush flex items-center justify-center">
                    <Receipt size={16} />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-display text-lg">
                      {o.kind === "shop"
                        ? `Order #${o.order_number} · ${(o.items || []).length} item${(o.items || []).length > 1 ? "s" : ""}`
                        : o.course_name || o.lookup_key}
                    </div>
                    <div className="text-xs text-[hsl(var(--onyx)/0.5)] mt-1">
                      {new Date(o.created_at).toLocaleString()} · {o.currency?.toUpperCase()} {Number(o.amount).toFixed(2)}
                    </div>
                  </div>
                  <span className="text-xs uppercase tracking-widest px-3 py-1 rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]">
                    Paid
                  </span>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "wishlist" && (
          wishlistProducts.length === 0 ? (
            <div className="rounded-[2rem] border border-dashed border-[hsl(var(--onyx)/0.15)] p-12 text-center bg-white">
              <Heart size={22} className="text-[hsl(var(--gold))] mx-auto mb-4" />
              <div className="overline">Your wishlist</div>
              <h2 className="font-display text-3xl mt-3">Save your favourites.</h2>
              <p className="mt-2 text-sm text-[hsl(var(--onyx)/0.6)]">
                Tap the heart on any product to save it here.
              </p>
              <Link to="/shop" data-testid="wishlist-shop" className="btn-pill btn-primary mt-6 inline-flex">
                Explore Shop <ShoppingBag size={16} />
              </Link>
            </div>
          ) : (
            <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {wishlistProducts.map((p) => (
                <Item key={p.slug}>
                  <div
                    data-testid={`wishlist-item-${p.slug}`}
                    className="rounded-[2rem] overflow-hidden bg-white border border-[hsl(var(--onyx)/0.06)]"
                  >
                    <Link to={`/shop/${p.slug}`} className="block hover-zoom aspect-square bg-blush">
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                    </Link>
                    <div className="p-6">
                      <div className="font-display text-xl">{p.name}</div>
                      <div className="mt-2 font-display text-lg">£{p.price.toFixed(2)}</div>
                      <div className="mt-5 flex items-center gap-2">
                        <Link to={`/shop/${p.slug}`} className="btn-pill btn-primary !text-xs !py-2 flex-1">
                          View
                        </Link>
                        <button
                          onClick={() => removeFromWishlist(p.slug)}
                          className="btn-pill btn-outline !text-xs !py-2"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </Item>
              ))}
            </Stagger>
          )
        )}
      </div>
    </div>
  );
}
